import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/storefront/Navbar";
import Footer from "@/components/storefront/Footer";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Plus, Minus, ShoppingBag, Check } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { api } from "@/lib/api";
import { formatSAR } from "@/lib/i18n";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang, addToCart } = useApp();
  const [product, setProduct] = useState(null);
  const [variantId, setVariantId] = useState(null);
  const [serviceId, setServiceId] = useState(null);
  const [qty, setQty] = useState(1);
  const [customerInfo, setCustomerInfo] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        // Default selections
        if (r.data?.variants?.length) setVariantId(r.data.variants[0].id);
        if (r.data?.service_options?.length) setServiceId(r.data.service_options[0].id);
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [id]);

  const Arrow = lang === "ar" ? ChevronRight : ChevronLeft;

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 max-w-7xl mx-auto px-4 py-16 w-full">
          <div className="animate-pulse h-96 bg-surface rounded-xl" />
        </div>
      </div>
    );
  }
  if (!product) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          Product not found
        </div>
      </div>
    );
  }

  const name = lang === "ar" ? product.name_ar : product.name_en;
  const desc = lang === "ar" ? product.description_ar : product.description_en;
  const category = lang === "ar" ? product.category_ar : product.category_en;
  const selectedVariant = (product.variants || []).find((v) => v.id === variantId);
  const selectedService = (product.service_options || []).find((s) => s.id === serviceId);
  const unitPrice =
    Number(product.base_price) +
    Number(selectedVariant?.price_modifier || 0) +
    Number(selectedService?.price_addon || 0);
  const previewImage = selectedVariant?.preview_image || product.preview_image;

  const handleAdd = (goToCart = false) => {
    if (!serviceId) {
      toast.error(t.selectServiceFirst);
      return;
    }
    if (!variantId) {
      toast.error(t.selectStyleFirst);
      return;
    }
    if (selectedService?.requires_info && !customerInfo.trim()) {
      toast.error(t.customerInfoHint);
      return;
    }
    addToCart(product, variantId, serviceId, qty, customerInfo);
    toast.success(lang === "ar" ? "تمت الإضافة للسلة" : "Added to cart", { description: name });
    if (goToCart) navigate("/cart");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        {/* Breadcrumb */}
        <nav className="text-sm text-muted-foreground mb-6 flex items-center gap-1">
          <Link to="/" className="hover:text-foreground" data-testid="breadcrumb-home">
            {t.breadcrumbHome}
          </Link>
          <Arrow className="h-3.5 w-3.5" />
          <span className="text-foreground">{name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-12">
          <div className="space-y-3">
            <div className="aspect-[3/4] rounded-xl overflow-hidden bg-muted border border-border">
              <img src={previewImage} alt={name} className="h-full w-full object-cover" />
            </div>
            {/* Variant thumbnails */}
            {product.variants?.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {product.variants.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setVariantId(v.id)}
                    data-testid={`variant-thumb-${v.id}`}
                    className={`aspect-square rounded-md overflow-hidden border-2 transition-all ${
                      variantId === v.id ? "border-primary" : "border-border hover:border-muted-foreground"
                    }`}
                  >
                    <img src={v.preview_image} alt={v.name_en} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-5">
            <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
              {category} · SKU {product.sku}
            </span>
            <h1 data-testid="product-detail-name" className="text-3xl sm:text-4xl font-bold tracking-tight">
              {name}
            </h1>
            <p className="text-muted-foreground leading-relaxed">{desc}</p>

            <p className="text-3xl font-bold text-primary" data-testid="product-detail-price">
              {formatSAR(unitPrice, lang)}
            </p>

            {/* Style selection */}
            {product.variants?.length > 0 && (
              <div data-testid="variant-selector">
                <label className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
                  {t.style}
                </label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {product.variants.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setVariantId(v.id)}
                      data-testid={`variant-option-${v.id}`}
                      className={`flex items-center justify-between gap-2 px-4 py-3 rounded-lg border transition-all text-sm font-medium ${
                        variantId === v.id
                          ? "border-primary bg-accent text-accent-foreground shadow-sm"
                          : "border-border bg-surface hover:bg-muted"
                      }`}
                    >
                      <span>{lang === "ar" ? v.name_ar : v.name_en}</span>
                      {variantId === v.id && <Check className="h-4 w-4" />}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Service option selection */}
            {product.service_options?.length > 0 && (
              <div data-testid="service-selector">
                <label className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
                  {t.serviceType}
                </label>
                <div className="mt-2 space-y-2">
                  {product.service_options.map((s) => {
                    const active = serviceId === s.id;
                    return (
                      <button
                        key={s.id}
                        onClick={() => setServiceId(s.id)}
                        data-testid={`service-option-${s.id}`}
                        className={`w-full text-start flex items-start gap-3 px-4 py-3 rounded-lg border transition-all ${
                          active
                            ? "border-primary bg-accent text-accent-foreground shadow-sm"
                            : "border-border bg-surface hover:bg-muted"
                        }`}
                      >
                        <div
                          className={`mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            active ? "border-primary" : "border-muted-foreground"
                          }`}
                        >
                          {active && <div className="h-2 w-2 bg-primary rounded-full" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium text-sm">
                              {lang === "ar" ? s.name_ar : s.name_en}
                            </span>
                            {Number(s.price_addon) > 0 && (
                              <span className="text-xs font-bold text-primary">
                                +{formatSAR(s.price_addon, lang)}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                            {lang === "ar" ? s.description_ar : s.description_en}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Customer info textbox — shown only when service requires it (done_for_you) */}
            {selectedService?.requires_info && (
              <div data-testid="customer-info-block" className="space-y-2 animate-fade-up">
                <label className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
                  {t.customerInfoLabel}
                </label>
                <textarea
                  data-testid="customer-info-textarea"
                  value={customerInfo}
                  onChange={(e) => setCustomerInfo(e.target.value)}
                  placeholder={t.customerInfoPlaceholder}
                  rows={6}
                  className="w-full rounded-lg border border-primary/40 bg-accent/30 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 resize-y"
                />
                <p className="text-[11px] text-muted-foreground">{t.customerInfoHint}</p>
              </div>
            )}

            {/* Quantity + actions */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <div className="inline-flex items-center bg-surface border border-border rounded-full">
                <Button variant="ghost" size="icon" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-decrement">
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-10 text-center font-semibold" data-testid="qty-value">{qty}</span>
                <Button variant="ghost" size="icon" onClick={() => setQty((q) => q + 1)} data-testid="qty-increment">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <Button
                size="lg"
                className="gap-2 rounded-full flex-1 sm:flex-none"
                onClick={() => handleAdd(false)}
                data-testid="product-add-to-cart"
              >
                <ShoppingBag className="h-4 w-4" />
                {t.addToCart}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full"
                onClick={() => handleAdd(true)}
                data-testid="product-buy-now"
              >
                {t.checkout}
              </Button>
            </div>
            <div className="text-xs text-muted-foreground border-t border-border pt-4">
              {lang === "ar"
                ? "تنزيل فوري بعد الدفع · متوافق مع Word و PDF"
                : "Instant download after payment · Word & PDF compatible"}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
